import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";
import TextField from "@/components/common/text-field";
import SelectField from "@/components/common/select";
import Variables from "@/workflow_api/components/variables";
import Empty from "@/workflow_api/components/variables/empty";
import {
  deserializeWaitInputs,
  serializeWaitInputs,
  serializeWaitEdges,
  defaultWaitConfig,
  type WaitInput,
} from "./serialization";
import { v4 as uuid } from "uuid";
import {
  waitNodeSchema,
  type WaitNodeFormData,
} from "@/schemas/workflow/wait-node";
import { NodeTypes } from "@/workflow_api/types/workflow";
import { Icon } from "@elaniintc/goes-design-system";

interface WaitDetailsProps {
  initialTitle?: string;
}

export const WaitDetails: React.FC<WaitDetailsProps> = ({
  initialTitle = "Wait",
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);

  const {
    nodeDetailsOpen,
    selectedNodeId,
    setNodeDetailsOpen,
    setSelectedNodeId,
  } = useUIStore();

  const { getNodeById, flowId, nodes } = useWorkflowStore();
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);

  const selectedNode = useMemo(
    () => getNodeById(selectedNodeId || ""),
    [getNodeById, selectedNodeId],
  );
  const nodeData = selectedNode?.data as unknown as NodeData | undefined;

  // Obtener todos los nodos multithread disponibles
  const multithreadNodes = useMemo(() => {
    return nodes
      .filter((node: { data: unknown }) => {
        const data = node.data as unknown as NodeData;
        return data.type === NodeTypes.MULTITHREAD;
      })
      .map((node: { id: string; data: unknown }) => ({
        value: node.id,
        label: (node.data as unknown as NodeData).displayName,
      }));
  }, [nodes]);

  // ID del nodo multithread seleccionado
  const [selectedMultithreadId, setSelectedMultithreadId] =
    useState<string>("");

  // Lista de inputs del Wait (cada uno representa una entrada)
  const [waitInputs, setWaitInputs] = useState<WaitInput[]>([]);

  // Inicializar desde nodeData
  useEffect(() => {
    if (nodeData?.inputs) {
      // Pasar también los edges para obtener el source_handler correcto
      const deserialized = deserializeWaitInputs(
        nodeData.inputs,
        nodeData.edges,
      );
      setWaitInputs(deserialized);

      // Intentar detectar el multithread asociado desde los inputs
      const multithreadInput = nodeData.inputs.find(
        (input) => input.name === "multithread_id",
      );
      if (multithreadInput && multithreadInput.value) {
        setSelectedMultithreadId(multithreadInput.value as string);
      }
    }
  }, [nodeData?.inputs, getNodeById, nodeData?.edges]);

  // Obtener las salidas (threads) del multithread seleccionado
  const multithreadOutputs = useMemo(() => {
    if (!selectedMultithreadId) return [];

    const multithreadNode = getNodeById(selectedMultithreadId);
    if (!multithreadNode) return [];

    const multithreadData = multithreadNode.data as unknown as NodeData;
    if (!multithreadData.connections) return [];

    return Object.entries(multithreadData.connections)
      .filter(([key]) => key.startsWith("thread_"))
      .sort((a, b) => {
        const aNum = parseInt(a[0].split("_")[1] || "0", 10);
        const bNum = parseInt(b[0].split("_")[1] || "0", 10);
        return aNum - bNum;
      })
      .map(([key, value]) => ({
        key,
        label: value.display_name || key,
      }));
  }, [selectedMultithreadId, getNodeById]);

  // Cuando cambia el multithread seleccionado, actualizar los inputs
  useEffect(() => {
    if (!selectedMultithreadId || multithreadOutputs.length === 0) {
      return;
    }

    // Crear inputs basados en las salidas del multithread
    // Si ya existen inputs, preservar sus conexiones cuando sea posible
    setWaitInputs((prevInputs: WaitInput[]) => {
      return multithreadOutputs.map(
        (_output: { key: string; label: string }, index: number) => {
          // Buscar si ya existe un input con el mismo índice
          const existingInput = prevInputs[index];

          return {
            id: existingInput?.id || uuid(),
            name: existingInput?.name || `Entrada ${index + 1}`,
            // Preservar conexiones existentes, no asignar automáticamente
            sourceConnections: existingInput?.sourceConnections || [],
          };
        },
      );
    });
  }, [selectedMultithreadId, multithreadOutputs]);

  const { mutateAsync: updateNode, isPending: isSaving } =
    useUpdateNode(flowId);

  // Form para configuración base (no se usa mucho actualmente)
  const form = useForm<WaitNodeFormData>({
    resolver: zodResolver(waitNodeSchema),
    defaultValues: defaultWaitConfig,
    mode: "onChange",
  });

  const {
    trigger,
    formState: { isValid },
  } = form;

  // Actualizar título desde nodeData
  useEffect(() => {
    if (nodeData?.displayName) {
      setTitle(nodeData.displayName);
    } else {
      setTitle(initialTitle);
    }
  }, [initialTitle, nodeData?.displayName]);

  // Manejar cambio de nombre de input
  const handleInputNameChange = (inputId: string, newName: string) => {
    setWaitInputs((prev: WaitInput[]) =>
      prev.map((input: WaitInput) =>
        input.id === inputId ? { ...input, name: newName } : input,
      ),
    );
  };

  // Guardar cambios
  const handleSave = async () => {
    if (!selectedNodeId || !selectedNode || !nodeData) return;

    setShowNodeTitleValidation(true);
    if (!isNameValid(title)) return;

    const isFormValid = await trigger();
    if (!isFormValid) return;

    // Serializar inputs para el backend
    const serializedInputs = serializeWaitInputs(
      waitInputs,
      selectedMultithreadId ?? "",
    );

    // Generar edges
    const edges = serializeWaitEdges(waitInputs);

    await updateNode({
      node_id: selectedNodeId,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: selectedNode.position.x,
        position_y: selectedNode.position.y,
        type: nodeData.node_type,
        edges: edges.length > 0 ? edges : undefined,
        inputs: serializedInputs,
      },
    });

    setNodeDetailsOpen(false);
    setSelectedNodeId(undefined);
  };

  const leftContent = (
    <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)]">
      {selectedNodeId ? <Variables sourceNodeId={selectedNodeId} /> : <Empty />}
    </div>
  );

  const centerContent = (
    <div className="flex flex-col gap-6 py-4">
      {/* Selección de nodo Multithread */}
      <section className="space-y-4 px-4">
        <SelectField
          name="multithread-node"
          label="Selección de Multi Hilos"
          placeholder="Selecciona un nodo Multi Hilos"
          value={selectedMultithreadId}
          options={multithreadNodes}
          required
          onChange={(value) => setSelectedMultithreadId(value)}
        />
      </section>
      <div className="border-b border-neutral" />
      <div className="px-4">
        {waitInputs.length > 0 && (
          <section className="space-y-4">
            <div className="space-y-3">
              {waitInputs.map((input: WaitInput, index: number) => {
                // Obtener info de las conexiones
                const connections = input.sourceConnections.map(
                  (conn: { sourceNodeId: string; sourceHandle: string }) => {
                    const sourceNode = getNodeById(conn.sourceNodeId);
                    const sourceName = sourceNode
                      ? (sourceNode.data as unknown as NodeData).displayName
                      : "Nodo desconocido";
                    return { sourceName, sourceHandle: conn.sourceHandle };
                  },
                );

                return (
                  <div
                    key={input.id || index}
                    //className="p-3 bg-white dark:bg-transparent rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-white"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <TextField
                          required
                          label={`Entrada ${index + 1}`}
                          value={input.name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleInputNameChange(
                              input.id || "",
                              e.target.value,
                            )
                          }
                          placeholder="Nombre de la entrada"
                          maxLength={50}
                        />
                      </div>
                    </div>

                    {/* Mostrar conexiones actuales */}
                    <div className="mt-2">
                      {connections.length > 0 ? (
                        <div className="text-xs text-body">
                          <span className="font-medium">Conectado desde: </span>
                          {connections.map(
                            (
                              conn: {
                                sourceName: string;
                                sourceHandle: string;
                              },
                              i: number,
                            ) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1"
                              >
                                {i > 0 && ", "}
                                <Icon
                                  type="far"
                                  name="plug"
                                  className="text-success-600"
                                />
                                {conn.sourceName} ({conn.sourceHandle})
                              </span>
                            ),
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-body italic">
                          Sin conexión - arrastra desde un nodo hacia esta
                          entrada
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );

  const rightContent = (
    <div className="bg-neutral-foreground overflow-auto divide-y divide-[var(--border-neutral-quiet)] h-full">
      <Empty />
    </div>
  );

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán."
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={isSaving}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId(undefined);
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={handleSave}
        isSaveValid={isValid && isNameValid(title)}
        isLoading={isSaving}
      >
        <ThreeColumnLayout
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
            hideSearch: true,
          }}
          centerHeader={{
            icon: "hourglass-half",
            title,
            color: "neutral",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContentWidth="min-w-[445px]"
          leftContent={leftContent}
          centerContent={centerContent}
          rightContent={rightContent}
        />
      </FullModal>
    </>
  );
};

export default WaitDetails;
